import { getCategorySlugsFromUrl } from './getCategorySlugsFromUrl';
import { knownPromotions } from '@utils/algolia/urlIsKnownFilter';
import {
  BRANDS,
  MARQUES,
  CANONICAL_BASE_BRAND_SLUGS,
} from '@utils/plp/constants';

const shopCanadian: Record<string, string[]> = {
  en: ['made-in-canada', 'canadian-based-outdoor-brands'],
  fr: ['fabriques-au-canada', 'marques-canadiennes'],
};

export const localizedProductsSlug = (locale = 'en') => {
  return locale === 'en' ? 'Products' : 'Produits';
};

export const isBrandPlp = (url: string) =>
  /(products|produits)\/(brands|marques)\//.test(url);

export const isCollectionPlp = (url: string) =>
  /(collections|assortiment)/.test(url);

export const isTagsPlp = (url: string) =>
  /(products|produits)\/tags\//.test(url);

export const isSearchPage = (url: string) => url.includes('/search');

export const isNewArrivalsPlp = (url: string) =>
  /\/(new-arrivals|nouveau)$/.test(url);

export const isPromotionPlp = (url: string, locale = 'en') => {
  const lastUrlSlug = url.split('/').at(-1) || '';
  // Add guard to prevent TypeError if knownPromotions[locale] is undefined
  return (
    Array.isArray(knownPromotions[locale]) &&
    knownPromotions[locale].includes(lastUrlSlug)
  );
};

export const isCanadianPlp = (url: string, locale = 'en') => {
  const { baseCategorySlug } =
    /(?:products|produits)\/(?<baseCategorySlug>[^\/]+)/.exec(url)?.groups ??
    {};
  return shopCanadian[locale].includes(baseCategorySlug);
};

export const isGenderedMale = (url: string, locale = 'en') => {
  const { categoryFilters } = getCategorySlugsFromUrl(url, locale);
  return Boolean(
    categoryFilters?.includes('mens') || categoryFilters?.includes('hommes')
  );
};

export const isGenderedFemale = (url: string, locale = 'en') => {
  const { categoryFilters } = getCategorySlugsFromUrl(url, locale);
  return Boolean(
    categoryFilters?.includes('womens') || categoryFilters?.includes('femmes')
  );
};

export const parseAlgoliaUrl = (url = '', locale = 'en') => {
  const { brandIdentifier, brandCategoryFilter } =
    /(products|produits)\/(brands|marques)\/(?<brandIdentifier>[^\/]+)\/?(?<brandCategoryFilter>.*)?$/.exec(
      url
    )?.groups ?? {};

  const isTaxonomyPlp =
    !Boolean(brandIdentifier) &&
    !isCollectionPlp(url) &&
    !isTagsPlp(url) &&
    !isSearchPage(url);

  return {
    isBrandPlp: Boolean(brandIdentifier && !brandCategoryFilter),
    isCollectionPlp: isCollectionPlp(url),
    isTagsPlp: isTagsPlp(url),
    isSearchPage: isSearchPage(url),
    isPromotionPlp: isPromotionPlp(url, locale),
    isTaxonomyPlp,
    brandIdentifier,
    brandCategoryFilter,
    isFilteredBrand: Boolean(brandCategoryFilter),
    urlFilters: getCategorySlugsFromUrl(url, locale),
    isGenderedMale: isGenderedMale(url, locale),
    isGenderedFemale: isGenderedFemale(url, locale),
  };
};

// ED-304: deep/filtered pages under a targeted brand should canonical back to the
// main brand PLP. True only when the page sits under one of the allow-listed
// brands AND has a category/filter sub-path (i.e. it is not the base brand page).
export const shouldCanonicalToBaseBrand = (
  brandIdentifier: string | undefined,
  isFilteredBrand: boolean
) =>
  Boolean(
    brandIdentifier &&
      isFilteredBrand &&
      CANONICAL_BASE_BRAND_SLUGS.includes(brandIdentifier.toLowerCase())
  );

// Locale-aware path (no host/locale prefix) of the main brand PLP a deep brand
// page canonicalizes to, e.g. `/products/brands/the-north-face` (en) or
// `/produits/marques/the-north-face` (fr).
export const baseBrandCanonicalPath = (
  brandIdentifier: string,
  locale = 'en'
) => {
  const brandSlug = brandIdentifier.toLowerCase();
  return locale === 'fr'
    ? `/produits/${MARQUES}/${brandSlug}`
    : `/products/${BRANDS}/${brandSlug}`;
};
